import { useQuery } from '@tanstack/react-query'
import { Users, ChevronDown, ChevronRight, CreditCard, Mail, Cloud, FileText, MessageSquare, ShieldCheck, ShieldAlert, ShieldX, KeyRound, AlertTriangle, Lightbulb, Bot } from 'lucide-react'
import { api, apiFetchJson } from '@/lib/api-client'
import { formatDate } from '@/lib/utils'
import { PageHeader } from '@/components/ui/page-header'
import { Card } from '@/components/ui/card'
import { Badge, StatusBadge, SeverityBadge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import { FilterBar, type FilterDef } from '@/components/data/filter-bar'
import { formatCurrency } from '@roi365/shared'
import { useState } from 'react'

interface M365User {
  id: string
  display_name: string
  user_principal_name: string
  department: string | null
  account_enabled: number
  last_sign_in_at: string | null
  license_count: number
}

interface UserLicense {
  id: string
  sku_part_number: string
  display_name: string
  monthly_cost_per_unit: number | null
}

function UserLicenses({ tenantId, userId }: { tenantId: string; userId: string }) {
  const { data: licenses, isLoading } = useQuery({
    queryKey: ['user-licenses', tenantId, userId],
    queryFn: () => api.get<UserLicense[]>(`/m365-tenants/${tenantId}/users/${userId}/licenses`),
  })

  if (isLoading) return <div className="animate-pulse h-6 w-32 rounded" style={{ backgroundColor: 'var(--color-gray-200)' }} />
  if (!licenses?.length) return <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Sin licencias asignadas</p>

  const totalCost = licenses.reduce((sum, l) => sum + (l.monthly_cost_per_unit ?? 0), 0)

  return (
    <div className="space-y-1.5">
      {licenses.map((l) => (
        <div key={l.id} className="flex items-center justify-between gap-4 rounded-md px-3 py-1.5" style={{ backgroundColor: 'var(--color-gray-50)' }}>
          <div className="flex items-center gap-2 min-w-0">
            <CreditCard className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--color-brand-secondary)' }} />
            <span className="text-sm truncate" style={{ color: 'var(--color-text-primary)' }}>{l.display_name}</span>
          </div>
          <span className="text-xs tabular-nums shrink-0" style={{ color: 'var(--color-text-tertiary)' }}>
            {l.monthly_cost_per_unit ? formatCurrency(l.monthly_cost_per_unit) + '/mes' : ''}
          </span>
        </div>
      ))}
      {totalCost > 0 && (
        <div className="flex justify-end pt-1">
          <span className="text-xs font-semibold tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
            Total: {formatCurrency(totalCost)}/mes
          </span>
        </div>
      )}
    </div>
  )
}

interface UserActivityData {
  report_period_days: number
  has_exchange_license: number
  exchange_last_activity: string | null
  send_count: number
  receive_count: number
  read_count: number
  has_onedrive_license: number
  onedrive_last_activity: string | null
  onedrive_files_viewed: number
  onedrive_files_synced: number
  onedrive_files_shared: number
  onedrive_storage_used_bytes: number
  has_sharepoint_license: number
  sharepoint_last_activity: string | null
  sharepoint_files_viewed: number
  sharepoint_files_synced: number
  sharepoint_files_shared: number
  sharepoint_pages_visited: number
  has_teams_license: number
  teams_last_activity: string | null
  teams_chat_count: number
  teams_call_count: number
  teams_meeting_count: number
  has_copilot_license: number
  copilot_last_activity: string | null
  copilot_active_apps_json: string | null
  copilot_active_app_count: number
  last_activity_date: string | null
  updated_at: string
}

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

function ActivityServiceCard({
  icon: Icon,
  title,
  hasLicense,
  lastActivity,
  metrics,
}: {
  icon: typeof Mail
  title: string
  hasLicense: boolean
  lastActivity: string | null
  metrics: Array<{ label: string; value: string | number }>
}) {
  const daysAgo = lastActivity
    ? Math.floor((Date.now() - new Date(lastActivity).getTime()) / (1000 * 60 * 60 * 24))
    : null
  const activityColor =
    daysAgo === null
      ? 'var(--color-text-tertiary)'
      : daysAgo > 90
        ? 'var(--color-error)'
        : daysAgo > 30
          ? 'var(--color-warning)'
          : 'var(--color-success-dark)'

  return (
    <div
      className="rounded-md border p-3"
      style={{
        borderColor: 'var(--color-border)',
        backgroundColor: hasLicense ? 'var(--color-surface)' : 'var(--color-gray-50)',
        opacity: hasLicense ? 1 : 0.6,
      }}
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4" style={{ color: 'var(--color-brand-secondary)' }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {title}
          </span>
        </div>
        {!hasLicense && (
          <span className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>
            Sin licencia
          </span>
        )}
      </div>
      <div className="mb-2 text-xs" style={{ color: activityColor }}>
        {lastActivity ? `Última actividad: ${formatDate(lastActivity)}` : 'Sin actividad registrada'}
      </div>
      <div className="space-y-1">
        {metrics.map((m) => (
          <div key={m.label} className="flex items-baseline justify-between gap-2 min-w-0">
            <span className="text-xs truncate" style={{ color: 'var(--color-text-tertiary)' }}>{m.label}</span>
            <span className="text-xs font-medium tabular-nums shrink-0" style={{ color: 'var(--color-text-primary)' }}>
              {m.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function UserActivity({ tenantId, userId }: { tenantId: string; userId: string }) {
  const { data: activity, isLoading } = useQuery({
    queryKey: ['user-activity', tenantId, userId],
    queryFn: () => api.get<UserActivityData | null>(`/m365-tenants/${tenantId}/users/${userId}/activity`),
  })

  if (isLoading) {
    return (
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-28 animate-pulse rounded-md" style={{ backgroundColor: 'var(--color-gray-100)' }} />
        ))}
      </div>
    )
  }

  if (!activity) {
    return (
      <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
        Sin datos de actividad. La actividad se sincroniza con el siguiente reporte D30.
      </p>
    )
  }

  // copilot_active_apps_json: Array<{ app, lastActivity }>, most-recent first.
  // Tolerate the legacy string[] shape from rows synced before this change.
  const copilotApps: Array<{ app: string; lastActivity: string | null }> = activity.copilot_active_apps_json
    ? (() => {
        try {
          const parsed = JSON.parse(activity.copilot_active_apps_json)
          if (!Array.isArray(parsed)) return []
          return parsed.map((item) =>
            typeof item === 'string' ? { app: item, lastActivity: null } : item,
          )
        } catch {
          return []
        }
      })()
    : []
  const hasCopilot = !!activity.has_copilot_license

  const copilotDaysAgo = activity.copilot_last_activity
    ? Math.floor((Date.now() - new Date(activity.copilot_last_activity).getTime()) / (1000 * 60 * 60 * 24))
    : null

  return (
    <div className="space-y-2">
      {/* Servicios M365 — fila uniforme */}
      <div className="grid items-start gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <ActivityServiceCard
          icon={Mail}
          title="Exchange"
          hasLicense={!!activity.has_exchange_license}
          lastActivity={activity.exchange_last_activity}
          metrics={[
            { label: 'Enviados', value: activity.send_count.toLocaleString('es-ES') },
            { label: 'Recibidos', value: activity.receive_count.toLocaleString('es-ES') },
            { label: 'Leídos', value: activity.read_count.toLocaleString('es-ES') },
          ]}
        />
        <ActivityServiceCard
          icon={Cloud}
          title="OneDrive"
          hasLicense={!!activity.has_onedrive_license}
          lastActivity={activity.onedrive_last_activity}
          metrics={[
            { label: 'Vistos', value: activity.onedrive_files_viewed.toLocaleString('es-ES') },
            { label: 'Sincronizados', value: activity.onedrive_files_synced.toLocaleString('es-ES') },
            { label: 'Compartidos', value: activity.onedrive_files_shared.toLocaleString('es-ES') },
            { label: 'Almacenamiento', value: formatBytes(activity.onedrive_storage_used_bytes) },
          ]}
        />
        <ActivityServiceCard
          icon={FileText}
          title="SharePoint"
          hasLicense={!!activity.has_sharepoint_license}
          lastActivity={activity.sharepoint_last_activity}
          metrics={[
            { label: 'Vistos', value: activity.sharepoint_files_viewed.toLocaleString('es-ES') },
            { label: 'Sincronizados', value: activity.sharepoint_files_synced.toLocaleString('es-ES') },
            { label: 'Compartidos', value: activity.sharepoint_files_shared.toLocaleString('es-ES') },
            { label: 'Páginas visitadas', value: activity.sharepoint_pages_visited.toLocaleString('es-ES') },
          ]}
        />
        <ActivityServiceCard
          icon={MessageSquare}
          title="Teams"
          hasLicense={!!activity.has_teams_license}
          lastActivity={activity.teams_last_activity}
          metrics={[
            { label: 'Mensajes chat', value: activity.teams_chat_count.toLocaleString('es-ES') },
            { label: 'Llamadas', value: activity.teams_call_count.toLocaleString('es-ES') },
            { label: 'Reuniones', value: activity.teams_meeting_count.toLocaleString('es-ES') },
          ]}
        />
      </div>

      {/* Copilot — tarjeta ancha aparte (IA) */}
      <CopilotActivityCard
        hasCopilot={hasCopilot}
        lastActivity={activity.copilot_last_activity}
        daysAgo={copilotDaysAgo}
        apps={copilotApps}
      />
    </div>
  )
}

function CopilotActivityCard({
  hasCopilot,
  lastActivity,
  daysAgo,
  apps,
}: {
  hasCopilot: boolean
  lastActivity: string | null
  daysAgo: number | null
  apps: Array<{ app: string; lastActivity: string | null }>
}) {
  const activityColor =
    daysAgo === null
      ? 'var(--color-text-tertiary)'
      : daysAgo > 90
        ? 'var(--color-error)'
        : daysAgo > 30
          ? 'var(--color-warning)'
          : 'var(--color-success-dark)'

  return (
    <div
      className="rounded-md border p-3"
      style={{
        borderColor: 'var(--color-border)',
        backgroundColor: hasCopilot ? 'var(--color-surface)' : 'var(--color-gray-50)',
        opacity: hasCopilot ? 1 : 0.6,
      }}
    >
      {/* Cabecera homogénea con las demás fichas */}
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4" style={{ color: 'var(--color-brand-secondary)' }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Copilot
          </span>
        </div>
        {!hasCopilot && (
          <span className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>
            Sin Copilot
          </span>
        )}
      </div>
      <div className="mb-2 text-xs" style={{ color: activityColor }}>
        {lastActivity ? `Última actividad: ${formatDate(lastActivity)}` : 'Sin actividad registrada'}
      </div>

      {/* Apps en fila */}
      {hasCopilot &&
        (apps.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {apps.map(({ app, lastActivity }) => (
              <span
                key={app}
                className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-[11px]"
                style={{ backgroundColor: 'var(--color-gray-100)', color: 'var(--color-text-secondary)' }}
              >
                <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{app}</span>
                {lastActivity && (
                  <span className="tabular-nums" style={{ color: 'var(--color-text-tertiary)' }}>
                    {formatDate(lastActivity)}
                  </span>
                )}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs" style={{ color: 'var(--color-warning)' }}>
            Licencia Copilot sin uso en los últimos 30 días
          </p>
        ))}
    </div>
  )
}

interface UserSecurityData {
  is_mfa_registered: number | null
  is_mfa_capable: number | null
  mfa_methods_json: string | null
  is_admin: number | null
  risk_level: string | null
  risk_state: string | null
  last_password_change_at: string | null
  security_synced_at: string | null
  account_enabled: number | null
  last_sign_in_at: string | null
}

function UserSecurity({ tenantId, userId }: { tenantId: string; userId: string }) {
  const { data: security, isLoading } = useQuery({
    queryKey: ['user-security', tenantId, userId],
    queryFn: () => api.get<UserSecurityData | null>(`/m365-tenants/${tenantId}/users/${userId}/security`),
  })

  if (isLoading) {
    return (
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-28 animate-pulse rounded-md" style={{ backgroundColor: 'var(--color-gray-100)' }} />
        ))}
      </div>
    )
  }

  if (!security) {
    return (
      <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
        Sin datos de seguridad. Se sincronizan tras el siguiente sync (requiere permisos Reports.Read.All e IdentityRiskyUser.Read.All).
      </p>
    )
  }

  if (!security.security_synced_at) {
    return (
      <p className="text-xs" style={{ color: 'var(--color-warning)' }}>
        Datos de seguridad pendientes de sincronizacion. Lanza un sync manual o espera al programado.
      </p>
    )
  }

  // MFA
  const mfaMethods: string[] = security.mfa_methods_json
    ? (() => {
        try {
          const parsed = JSON.parse(security.mfa_methods_json)
          return Array.isArray(parsed) ? parsed : []
        } catch {
          return []
        }
      })()
    : []
  const mfaRegistered = security.is_mfa_registered === 1
  const mfaCapable = security.is_mfa_capable === 1

  // Risk
  const riskLevel = security.risk_level ?? 'none'
  const riskState = security.risk_state ?? 'none'
  const isAtRisk = riskLevel === 'high' || riskLevel === 'medium' || riskState === 'atRisk' || riskState === 'confirmedCompromised'

  // Password age
  const pwdAgeDays = security.last_password_change_at
    ? Math.floor((Date.now() - new Date(security.last_password_change_at).getTime()) / (1000 * 60 * 60 * 24))
    : null

  const isAdmin = security.is_admin === 1

  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
      {/* MFA */}
      <div
        className="rounded-md border p-3"
        style={{
          borderColor: 'var(--color-border)',
          backgroundColor: !mfaRegistered && isAdmin ? 'var(--color-error-light)' : 'var(--color-surface)',
        }}
      >
        <div className="mb-2 flex items-center gap-2">
          {mfaRegistered ? (
            <ShieldCheck className="h-4 w-4" style={{ color: 'var(--color-success-dark)' }} />
          ) : (
            <ShieldX className="h-4 w-4" style={{ color: 'var(--color-error)' }} />
          )}
          <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>MFA</span>
        </div>
        <div className="space-y-1">
          <div className="flex items-baseline justify-between gap-2 min-w-0">
            <span className="text-xs truncate" style={{ color: 'var(--color-text-tertiary)' }}>Estado</span>
            <span className="text-xs font-medium shrink-0" style={{ color: mfaRegistered ? 'var(--color-success-dark)' : 'var(--color-error)' }}>
              {mfaRegistered ? 'Registrado' : 'No registrado'}
            </span>
          </div>
          {mfaCapable !== mfaRegistered && (
            <div className="flex items-baseline justify-between gap-2 min-w-0">
              <span className="text-xs truncate" style={{ color: 'var(--color-text-tertiary)' }}>Capacitado</span>
              <span className="text-xs font-medium shrink-0">{mfaCapable ? 'Si' : 'No'}</span>
            </div>
          )}
          {mfaMethods.length > 0 && (
            <div>
              <span className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Metodos</span>
              <p className="text-xs font-mono" style={{ color: 'var(--color-text-primary)' }}>
                {mfaMethods.join(', ')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Cuenta / Admin */}
      <div className="rounded-md border p-3" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
        <div className="mb-2 flex items-center gap-2">
          <KeyRound className="h-4 w-4" style={{ color: isAdmin ? 'var(--color-warning)' : 'var(--color-brand-secondary)' }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Cuenta</span>
        </div>
        <div className="space-y-1">
          <div className="flex items-baseline justify-between gap-2 min-w-0">
            <span className="text-xs truncate" style={{ color: 'var(--color-text-tertiary)' }}>Tipo</span>
            <span className="text-xs font-medium shrink-0" style={{ color: isAdmin ? 'var(--color-warning)' : 'var(--color-text-primary)' }}>
              {isAdmin ? 'Administrador' : 'Estandar'}
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-2 min-w-0">
            <span className="text-xs truncate" style={{ color: 'var(--color-text-tertiary)' }}>Habilitada</span>
            <span className="text-xs font-medium shrink-0">{security.account_enabled === 1 ? 'Si' : 'No'}</span>
          </div>
          {security.last_sign_in_at && (
            <div className="flex items-baseline justify-between gap-2 min-w-0">
              <span className="text-xs truncate" style={{ color: 'var(--color-text-tertiary)' }}>Ult. acceso</span>
              <span className="text-xs font-medium shrink-0 tabular-nums">{formatDate(security.last_sign_in_at)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Riesgo */}
      <div
        className="rounded-md border p-3"
        style={{
          borderColor: 'var(--color-border)',
          backgroundColor: isAtRisk ? 'var(--color-error-light)' : 'var(--color-surface)',
        }}
      >
        <div className="mb-2 flex items-center gap-2">
          {isAtRisk ? (
            <ShieldAlert className="h-4 w-4" style={{ color: 'var(--color-error)' }} />
          ) : (
            <ShieldCheck className="h-4 w-4" style={{ color: 'var(--color-success-dark)' }} />
          )}
          <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Riesgo</span>
        </div>
        <div className="space-y-1">
          <div className="flex items-baseline justify-between gap-2 min-w-0">
            <span className="text-xs truncate" style={{ color: 'var(--color-text-tertiary)' }}>Nivel</span>
            <span
              className="text-xs font-medium shrink-0 capitalize"
              style={{
                color:
                  riskLevel === 'high'
                    ? 'var(--color-error)'
                    : riskLevel === 'medium'
                      ? 'var(--color-warning)'
                      : riskLevel === 'low'
                        ? 'var(--color-text-primary)'
                        : 'var(--color-text-tertiary)',
              }}
            >
              {riskLevel === 'none' || riskLevel === 'hidden' ? 'Sin riesgo' : riskLevel}
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-2 min-w-0">
            <span className="text-xs truncate" style={{ color: 'var(--color-text-tertiary)' }}>Estado</span>
            <span className="text-xs font-medium shrink-0 capitalize">{riskState === 'none' ? '—' : riskState}</span>
          </div>
        </div>
      </div>

      {/* Password */}
      <div
        className="rounded-md border p-3"
        style={{
          borderColor: 'var(--color-border)',
          backgroundColor: pwdAgeDays !== null && pwdAgeDays > 365 ? 'var(--color-warning-light)' : 'var(--color-surface)',
        }}
      >
        <div className="mb-2 flex items-center gap-2">
          {pwdAgeDays !== null && pwdAgeDays > 365 ? (
            <AlertTriangle className="h-4 w-4" style={{ color: 'var(--color-warning)' }} />
          ) : (
            <KeyRound className="h-4 w-4" style={{ color: 'var(--color-brand-secondary)' }} />
          )}
          <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Contraseña</span>
        </div>
        <div className="space-y-1">
          {security.last_password_change_at ? (
            <>
              <div className="flex items-baseline justify-between gap-2 min-w-0">
                <span className="text-xs truncate" style={{ color: 'var(--color-text-tertiary)' }}>Ult. cambio</span>
                <span className="text-xs font-medium shrink-0 tabular-nums">
                  {formatDate(security.last_password_change_at)}
                </span>
              </div>
              <div className="flex items-baseline justify-between gap-2 min-w-0">
                <span className="text-xs truncate" style={{ color: 'var(--color-text-tertiary)' }}>Antigüedad</span>
                <span
                  className="text-xs font-medium shrink-0 tabular-nums"
                  style={{
                    color:
                      pwdAgeDays! > 365
                        ? 'var(--color-warning)'
                        : pwdAgeDays! > 180
                          ? 'var(--color-text-primary)'
                          : 'var(--color-text-tertiary)',
                  }}
                >
                  {pwdAgeDays} dias
                </span>
              </div>
            </>
          ) : (
            <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Sin datos</p>
          )}
        </div>
      </div>
    </div>
  )
}

interface UserRecommendation {
  id: string
  type: string
  severity: string
  title: string
  description: string
  estimated_monthly_saving: number
  status: string
  notes: string | null
  created_at: string
  updated_at: string
  source: 'rule_legacy' | 'ai'
  license_display_name: string | null
}

const recTypeLabels: Record<string, string> = {
  remove_license: 'Eliminar licencia',
  downgrade: 'Downgrade',
  disable_service: 'Deshabilitar servicio',
  reduce_purchased: 'Reducir compradas',
  remove_redundant: 'Eliminar redundante',
  alert: 'Alerta',
  alert_exchange: 'Infrautilización Exchange',
  alert_onedrive: 'Infrautilización OneDrive',
  alert_sharepoint: 'Infrautilización SharePoint',
  alert_teams: 'Infrautilización Teams',
  alert_copilot: 'Copilot sin uso',
}

const recStatusMap: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'info' | 'neutral' }> = {
  pending: { label: 'Pendiente', variant: 'warning' },
  accepted: { label: 'Aceptada', variant: 'info' },
  applied: { label: 'Aplicada', variant: 'success' },
  dismissed: { label: 'Descartada', variant: 'neutral' },
  resolved: { label: 'Resuelta', variant: 'success' },
  expired: { label: 'Expirada', variant: 'neutral' },
}

function UserRecommendations({ tenantId, userId }: { tenantId: string; userId: string }) {
  const { data: recs, isLoading } = useQuery({
    queryKey: ['user-recommendations', tenantId, userId],
    queryFn: () => api.get<UserRecommendation[]>(`/m365-tenants/${tenantId}/users/${userId}/recommendations`),
  })

  if (isLoading) {
    return <div className="h-16 animate-pulse rounded-md" style={{ backgroundColor: 'var(--color-gray-100)' }} />
  }

  if (!recs?.length) {
    return (
      <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
        Sin recomendaciones para este usuario.
      </p>
    )
  }

  const totalPendingSaving = recs
    .filter((r) => r.status === 'pending')
    .reduce((sum, r) => sum + (r.estimated_monthly_saving ?? 0), 0)

  return (
    <div className="space-y-2">
      {totalPendingSaving > 0 && (
        <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
          Ahorro pendiente:{' '}
          <span className="font-semibold tabular-nums" style={{ color: 'var(--color-brand-accent-2)' }}>
            {formatCurrency(totalPendingSaving)}/mes
          </span>
        </p>
      )}
      {recs.map((r) => {
        const statusEntry = recStatusMap[r.status] ?? { label: r.status, variant: 'neutral' as const }
        return (
          <div
            key={r.id}
            className="rounded-md border p-3"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <SeverityBadge severity={r.severity} />
                  <Badge variant="neutral">{recTypeLabels[r.type] ?? r.type}</Badge>
                  <Badge variant={statusEntry.variant}>{statusEntry.label}</Badge>
                  {r.source === 'ai' && <Badge variant="premium">IA</Badge>}
                  {r.license_display_name && (
                    <span className="text-xs font-medium" style={{ color: 'var(--color-brand-secondary)' }}>
                      {r.license_display_name}
                    </span>
                  )}
                </div>
                <p className="mt-1.5 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  {r.title}
                </p>
                <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  {r.description}
                </p>
                {r.notes && (
                  <div
                    className="mt-1.5 rounded px-2 py-1 text-xs"
                    style={{ backgroundColor: 'var(--color-gray-50)', color: 'var(--color-text-secondary)' }}
                  >
                    <MessageSquare className="mr-1 inline h-3 w-3" style={{ color: 'var(--color-text-tertiary)' }} />
                    {r.notes}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-0.5 shrink-0">
                {r.estimated_monthly_saving > 0 && (
                  <p className="text-sm font-bold tabular-nums" style={{ color: 'var(--color-brand-accent-2)' }}>
                    {formatCurrency(r.estimated_monthly_saving)}/mes
                  </p>
                )}
                <p className="text-[10px] tabular-nums" style={{ color: 'var(--color-text-tertiary)' }}>
                  {formatDate(r.created_at)}
                </p>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function UsersPage() {
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(50)
  const [filterValues, setFilterValues] = useState<Record<string, string>>(() => {
    const params = new URLSearchParams(window.location.search)
    const initial: Record<string, string> = {}
    if (params.get('name')) initial.name = params.get('name')!
    if (params.get('tenant')) initial.tenant = params.get('tenant')!
    if (params.get('email')) initial.email = params.get('email')!
    return initial
  })
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data: tenants } = useQuery({
    queryKey: ['m365-tenants'],
    queryFn: () => api.get<Array<{ id: string; display_name: string }>>('/m365-tenants'),
  })

  const selectedTenantId = filterValues.tenant || tenants?.[0]?.id || ''

  const userFilters: FilterDef[] = [
    { key: 'name', label: 'Nombre', type: 'search', placeholder: 'Filtrar por nombre...' },
    { key: 'email', label: 'Email', type: 'search', placeholder: 'Filtrar por email...' },
    { key: 'dept', label: 'Departamento', type: 'search', placeholder: 'Filtrar por departamento...' },
    {
      key: 'accountEnabled', label: 'Estado', type: 'select' as const,
      options: [{ value: 'true', label: 'Activos' }, { value: 'false', label: 'Deshabilitados' }],
    },
    ...(tenants && tenants.length > 1
      ? [{
          key: 'tenant',
          label: 'Tenant',
          type: 'select' as const,
          options: tenants.map((t) => ({ value: t.id, label: t.display_name })),
        }]
      : []),
  ]

  const { data: usersResponse, isLoading } = useQuery({
    queryKey: ['users', selectedTenantId, page, perPage, filterValues],
    queryFn: async () => {
      if (!selectedTenantId) return null
      const params = new URLSearchParams({ page: String(page), perPage: String(perPage) })
      if (filterValues.name) params.set('name', filterValues.name)
      if (filterValues.email) params.set('email', filterValues.email)
      if (filterValues.dept) params.set('dept', filterValues.dept)
      if (filterValues.accountEnabled) params.set('accountEnabled', filterValues.accountEnabled)
      return apiFetchJson<{
        ok: boolean
        data: M365User[]
        meta: { page: number; perPage: number; total: number; totalPages: number }
      }>(`/m365-tenants/${selectedTenantId}/users?${params}`)
    },
    enabled: !!selectedTenantId,
  })

  const users = usersResponse?.data ?? []

  return (
    <div className="space-y-6">
      <PageHeader title="Usuarios" description="Vista unificada de usuarios Microsoft 365" />

      <FilterBar
        filters={userFilters}
        values={filterValues}
        onChange={(k, v) => { setFilterValues((prev) => ({ ...prev, [k]: v })); setPage(1) }}
        onClear={() => { setFilterValues({}); setPage(1) }}
      />

      {!selectedTenantId && !isLoading ? (
        <Card>
          <EmptyState icon={Users} title="Sin usuarios" description="Conecta un tenant M365 para importar usuarios" />
        </Card>
      ) : (
        <Card padding={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: 'var(--color-gray-50)' }}>
                  <th className="w-8 px-2 py-3" />
                  {['Nombre', 'Email', 'Departamento', 'Estado', 'Licencias', 'Ultimo acceso'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: 'var(--color-text-secondary)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  [...Array(8)].map((_, i) => (
                    <tr key={i}>
                      <td colSpan={7} className="px-4 py-4">
                        <div className="h-10 animate-pulse rounded" style={{ backgroundColor: 'var(--color-gray-100)' }} />
                      </td>
                    </tr>
                  ))
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center" style={{ color: 'var(--color-text-tertiary)' }}>
                      Sin usuarios encontrados
                    </td>
                  </tr>
                ) : (
                  users.map((u) => {
                    const isExpanded = expandedId === u.id
                    const daysAgo = u.last_sign_in_at
                      ? Math.floor((Date.now() - new Date(u.last_sign_in_at).getTime()) / (1000 * 60 * 60 * 24))
                      : null

                    return (
                      <tr key={u.id} className="group">
                        {/* Main row */}
                        <td className="px-2 py-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
                          <button
                            onClick={() => setExpandedId(isExpanded ? null : u.id)}
                            className="rounded p-1 hover:bg-gray-100"
                          >
                            {isExpanded
                              ? <ChevronDown className="h-4 w-4" style={{ color: 'var(--color-text-tertiary)' }} />
                              : <ChevronRight className="h-4 w-4" style={{ color: 'var(--color-text-tertiary)' }} />}
                          </button>
                        </td>
                        <td className="px-4 py-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
                          <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{u.display_name}</p>
                        </td>
                        <td className="px-4 py-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
                          <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{u.user_principal_name}</span>
                        </td>
                        <td className="px-4 py-3 border-t hidden lg:table-cell" style={{ borderColor: 'var(--color-border)' }}>
                          <span style={{ color: 'var(--color-text-secondary)' }}>{u.department ?? '—'}</span>
                        </td>
                        <td className="px-4 py-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
                          <StatusBadge status={u.account_enabled ? 'active' : 'disabled'} />
                        </td>
                        <td className="px-4 py-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
                          <span className="tabular-nums font-medium" style={{ color: u.license_count > 0 ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)' }}>
                            {u.license_count}
                          </span>
                        </td>
                        <td className="px-4 py-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
                          {u.last_sign_in_at ? (
                            <span style={{
                              color: daysAgo! > 90 ? 'var(--color-error)' : daysAgo! > 30 ? 'var(--color-warning)' : 'var(--color-text-primary)',
                            }}>
                              {formatDate(u.last_sign_in_at)}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--color-text-tertiary)' }}>Nunca</span>
                          )}
                        </td>

                        {/* Expanded licenses row - rendered as extra cells in same <tr> is not valid,
                            so we close and open a new <tr> via fragment */}
                      </tr>
                    )
                  }).flatMap((row, i) => {
                    const u = users[i]
                    const isExpanded = expandedId === u.id
                    if (!isExpanded) return [row]
                    return [
                      row,
                      <tr key={`${u.id}-licenses`}>
                        <td />
                        <td colSpan={6} className="px-4 pb-4 pt-0">
                          <div
                            className="space-y-4 rounded-lg border p-4"
                            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}
                          >
                            <div>
                              <p className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>
                                Licencias asignadas
                              </p>
                              <UserLicenses tenantId={selectedTenantId} userId={u.id} />
                            </div>
                            <div>
                              <p className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>
                                Actividad (últimos 30 días)
                              </p>
                              <UserActivity tenantId={selectedTenantId} userId={u.id} />
                            </div>
                            <div>
                              <p className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>
                                Seguridad
                              </p>
                              <UserSecurity tenantId={selectedTenantId} userId={u.id} />
                            </div>
                            <div>
                              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>
                                <Lightbulb className="h-3.5 w-3.5" />
                                Recomendaciones
                              </p>
                              <UserRecommendations tenantId={selectedTenantId} userId={u.id} />
                            </div>
                          </div>
                        </td>
                      </tr>,
                    ]
                  })
                )}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="flex items-center justify-between border-t px-4 py-3" style={{ borderColor: 'var(--color-border)' }}>
              <div className="flex items-center gap-3 text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                <span>{usersResponse?.meta?.total ?? 0} usuarios</span>
                <span className="hidden sm:inline">·</span>
                <label className="flex items-center gap-2">
                  <span className="hidden sm:inline">Filas por pagina</span>
                  <select
                    value={perPage}
                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1) }}
                    className="rounded-md border px-2 py-1 text-sm"
                    style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
                  >
                    {[10, 25, 50, 100].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </label>
              </div>
              {(usersResponse?.meta?.totalPages ?? 1) > 1 && (
                <div className="flex gap-1">
                  <button
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                    className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-40"
                    style={{ borderColor: 'var(--color-border)' }}
                  >
                    Anterior
                  </button>
                  <span className="flex items-center px-3 text-sm tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
                    {page} / {usersResponse?.meta?.totalPages ?? 1}
                  </span>
                  <button
                    disabled={page >= (usersResponse?.meta?.totalPages ?? 1)}
                    onClick={() => setPage(page + 1)}
                    className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-40"
                    style={{ borderColor: 'var(--color-border)' }}
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}
